import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  DocumentArrowDownIcon,
  TableCellsIcon,
  ChartBarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import { useAuth } from "./AuthProvider";
import { esSoloLectura } from "../utils/roles";
import { useVenue } from "./VenueProvider";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { downloadLeadsCSV } from "../utils/leadsCSV";
import { generateLeadStatsPDF } from "../utils/leadStatsPDF";
import { showAlert } from "../alertService";

const ACCENT = "#1b4d2e";

/* ── helpers (kept local; AdminStats versions are module-private) ───── */

const toJsDate = (val) => {
  if (val == null) return null;
  if (val instanceof Date) return val;
  if (typeof val.toDate === "function") return val.toDate();
  if (typeof val.seconds === "number") return new Date(val.seconds * 1000);
  if (typeof val === "string" || typeof val === "number") return new Date(val);
  return null;
};

// Contact form leads = origin missing or "venueboard" → bucket as "Venueboard".
const isContactFormLead = (origin) =>
  !origin || String(origin).trim().toLowerCase() === "venueboard";

const bucketOrigin = (origin) =>
  isContactFormLead(origin) ? "Venueboard" : String(origin).trim();

/* ── range presets ─────────────────────────────────────────────────── */

// Each preset returns { start, end } where end is exclusive.
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const RANGES = [
  {
    key: "30d",
    label: "30 días",
    bounds: () => {
      const end = new Date();
      const start = startOfDay(new Date(end.getTime() - 30 * 86400000));
      return { start, end };
    },
  },
  {
    key: "lastMonth",
    label: "Mes pasado",
    bounds: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start, end };
    },
  },
  {
    key: "90d",
    label: "90 días",
    bounds: () => {
      const end = new Date();
      const start = startOfDay(new Date(end.getTime() - 90 * 86400000));
      return { start, end };
    },
  },
  {
    key: "12m",
    label: "12 meses",
    bounds: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth() - 12, end.getDate());
      return { start, end };
    },
  },
  {
    key: "ytd",
    label: "Año en curso",
    bounds: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), 0, 1);
      return { start, end };
    },
  },
  {
    key: "all",
    label: "Todo el tiempo",
    bounds: () => ({ start: null, end: new Date() }),
  },
];

const DEFAULT_RANGE = "90d";
const rangeStorageKey = (venueId) => `venueboard_leadStatsRange_${venueId || "_"}`;

// Pie / bar palette. ACCENT first, then accessible companions.
const PALETTE = ["#1b4d2e", "#d97706", "#0369a1", "#7c3aed", "#dc2626", "#0891b2", "#65a30d", "#db2777", "#52525b"];

const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const pad2 = (n) => String(n).padStart(2, "0");
const dayKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const monthKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const monthLabel = (d) => `${MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
const dayLabel = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;

/**
 * Compute all the stats series the view (and the PDF) need from a list of leads.
 * Pure — no React, no localStorage, safe to call from a util.
 */
export function computeLeadStats({ leads, range, columnsConfig }) {
  const total = leads.length;
  let ganados = 0;
  let perdidos = 0;
  let converted = 0;
  let cotizaciones = 0;

  const originBuckets = new Map(); // label → { count, ganados, perdidos }
  const topicBuckets = new Map();  // lower → { label (preserve casing), count }
  const statusCounts = new Map();  // status key → count

  for (const l of leads) {
    if (l.status === "ganado") ganados += 1;
    else if (l.status === "perdido") perdidos += 1;
    if (l.convertedToEventId) converted += 1;
    cotizaciones += Array.isArray(l.cotizacionHistory) ? l.cotizacionHistory.length : 0;

    const obucket = bucketOrigin(l.origin);
    const oentry = originBuckets.get(obucket) || { label: obucket, count: 0, ganados: 0, perdidos: 0 };
    oentry.count += 1;
    if (l.status === "ganado") oentry.ganados += 1;
    if (l.status === "perdido") oentry.perdidos += 1;
    originBuckets.set(obucket, oentry);

    const topicRaw = String(l.topic || "").trim();
    if (topicRaw) {
      const tkey = topicRaw.toLowerCase();
      const tentry = topicBuckets.get(tkey) || { label: topicRaw, count: 0 };
      tentry.count += 1;
      topicBuckets.set(tkey, tentry);
    }

    if (l.status) {
      statusCounts.set(l.status, (statusCounts.get(l.status) || 0) + 1);
    }
  }

  const decided = ganados + perdidos;
  const closeRate = decided > 0 ? ganados / decided : null;
  const conversionRate = total > 0 ? converted / total : null;

  // Origin detail (for table). Sorted desc by count.
  const byOrigin = Array.from(originBuckets.values())
    .map((o) => {
      const dec = o.ganados + o.perdidos;
      return {
        ...o,
        pct: total > 0 ? o.count / total : 0,
        closeRate: dec > 0 ? o.ganados / dec : null,
      };
    })
    .sort((a, b) => b.count - a.count);

  // Top 5 topics.
  const byTopic = Array.from(topicBuckets.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Status funnel — iterate columnsConfig to preserve order; tack on any
  // statuses not in the config (defensive).
  const cols = Array.isArray(columnsConfig) ? columnsConfig : [];
  const seenKeys = new Set();
  const byStatus = [];
  for (const c of cols) {
    const count = statusCounts.get(c.key) || 0;
    byStatus.push({ key: c.key, label: c.label || c.key, count });
    seenKeys.add(c.key);
  }
  for (const [k, count] of statusCounts.entries()) {
    if (!seenKeys.has(k)) byStatus.push({ key: k, label: k, count });
  }

  // Time series — bucket by day for short ranges, by month for long ones.
  const start = range?.start || (leads.length ? new Date(Math.min(...leads.map((l) => (toJsDate(l.createdAt) || new Date()).getTime()))) : new Date());
  const end = range?.end || new Date();
  const spanMs = end - start;
  const spanDays = spanMs / 86400000;
  const bucketBy = spanDays <= 95 ? "day" : "month";

  const buckets = new Map();
  for (const l of leads) {
    const d = toJsDate(l.createdAt);
    if (!d) continue;
    const k = bucketBy === "day" ? dayKey(d) : monthKey(d);
    buckets.set(k, (buckets.get(k) || 0) + 1);
  }
  // Fill missing buckets with 0 so the chart has continuous bars.
  const byTime = [];
  const cursor = new Date(start);
  if (bucketBy === "day") {
    cursor.setHours(0, 0, 0, 0);
    while (cursor < end) {
      const k = dayKey(cursor);
      byTime.push({ key: k, label: dayLabel(cursor), count: buckets.get(k) || 0 });
      cursor.setDate(cursor.getDate() + 1);
    }
  } else {
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    while (cursor < end) {
      const k = monthKey(cursor);
      byTime.push({ key: k, label: monthLabel(cursor), count: buckets.get(k) || 0 });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  }

  // "Frase destacada" — top channel ≥ 5 leads AND close rate > 60%.
  let highlight = null;
  for (const o of byOrigin) {
    if (o.count >= 5 && o.closeRate != null && o.closeRate > 0.6) {
      highlight = {
        origin: o.label,
        closeRate: o.closeRate,
        count: o.count,
      };
      break; // byOrigin is already sorted by volume desc → biggest qualifying channel.
    }
  }

  return {
    total,
    ganados,
    perdidos,
    converted,
    cotizaciones,
    closeRate,
    conversionRate,
    byOrigin,
    byTopic,
    byStatus,
    byTime,
    bucketBy,
    highlight,
  };
}

/* ── KPI card ──────────────────────────────────────────────────────── */

const KpiCard = ({ label, value, hint }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
    <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
      {label}
    </div>
    <div className="mt-1 text-2xl font-bold text-zinc-900 dark:text-white">
      {value}
    </div>
    {hint && (
      <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>
    )}
  </div>
);

/* ── component ─────────────────────────────────────────────────────── */

const LeadStats = ({ forms, columnsConfig, venueId, venue }) => {
  const { userInfo, user } = useAuth();
  const { cotizacionConfig } = useVenue();
  const venueForPdf = useMemo(() => ({
    name: venue?.name,
    logoUrl: cotizacionConfig?.logoUrl || null,
  }), [venue?.name, cotizacionConfig?.logoUrl]);

  // Persist range per venue. localStorage in lazy initializer so SSR-safe-ish
  // (the app is CSR-only but still — cleaner).
  const [rangeKey, setRangeKey] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_RANGE;
    try {
      const stored = window.localStorage.getItem(rangeStorageKey(venueId));
      if (stored && RANGES.some((r) => r.key === stored)) return stored;
    } catch (_) { /* ignore */ }
    return DEFAULT_RANGE;
  });

  // When the venue changes, re-read the stored preference for that venue.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(rangeStorageKey(venueId));
      if (stored && RANGES.some((r) => r.key === stored)) setRangeKey(stored);
      else setRangeKey(DEFAULT_RANGE);
    } catch (_) { setRangeKey(DEFAULT_RANGE); }
  }, [venueId]);

  const updateRange = useCallback((next) => {
    setRangeKey(next);
    if (typeof window !== "undefined") {
      try { window.localStorage.setItem(rangeStorageKey(venueId), next); }
      catch (_) { /* ignore */ }
    }
  }, [venueId]);

  const range = useMemo(() => {
    const def = RANGES.find((r) => r.key === rangeKey) || RANGES.find((r) => r.key === DEFAULT_RANGE);
    return { key: def.key, label: def.label, ...def.bounds() };
  }, [rangeKey]);

  /* ── filter + compute ──────────────────────────────────────────── */

  const filteredLeads = useMemo(() => {
    return forms.filter((f) => {
      const created = toJsDate(f.createdAt);
      if (!created) return false;
      if (range.start && created < range.start) return false;
      if (range.end && created >= range.end) return false;
      return true;
    });
  }, [forms, range]);

  const stats = useMemo(
    () => computeLeadStats({ leads: filteredLeads, range, columnsConfig }),
    [filteredLeads, range, columnsConfig],
  );

  /* ── per-origin table sort state ───────────────────────────────── */

  const [sortKey, setSortKey] = useState("count");
  const [sortDir, setSortDir] = useState("desc");
  const sortedByOrigin = useMemo(() => {
    const arr = [...stats.byOrigin];
    arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      // null close rate goes to the bottom regardless of direction.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [stats.byOrigin, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  const SortHeader = ({ k, label, align = "left" }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (
          sortDir === "asc"
            ? <ChevronUpIcon className="h-3 w-3" />
            : <ChevronDownIcon className="h-3 w-3" />
        )}
      </span>
    </th>
  );

  const fmtPct = (v) =>
    v == null || Number.isNaN(v) ? "—" : `${Math.round(v * 100)}%`;

  /* ── exports (wired in later steps) ────────────────────────────── */

  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);

  const hasLeads = stats.total > 0;

  const handleExportCSV = useCallback(async () => {
    if (!hasLeads || exportingCSV) return;
    setExportingCSV(true);
    try {
      downloadLeadsCSV({
        leads: filteredLeads,
        venueName: venue?.name || "venue",
        rangeKey: range.key,
      });
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.LEAD_STATS_EXPORT_CSV,
        entity: "lead",
        details: { range: range.key, totalLeads: stats.total },
      }, userInfo, user);
    } catch (err) {
      console.error("CSV export failed:", err);
      showAlert("No se pudo descargar el CSV", "error");
    } finally {
      setExportingCSV(false);
    }
  }, [hasLeads, exportingCSV, filteredLeads, venue, range, venueId, stats.total, userInfo, user]);

  const handleExportPDF = useCallback(async () => {
    if (!hasLeads || exportingPDF) return;
    setExportingPDF(true);
    try {
      await generateLeadStatsPDF({
        venue: venueForPdf,
        range,
        stats,
      });
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.LEAD_STATS_EXPORT_PDF,
        entity: "lead",
        details: { range: range.key, totalLeads: stats.total },
      }, userInfo, user);
    } catch (err) {
      console.error("PDF export failed:", err);
      showAlert("No se pudo generar el PDF", "error");
    } finally {
      setExportingPDF(false);
    }
  }, [hasLeads, exportingPDF, venueForPdf, range, stats, venueId, userInfo, user]);

  /* ── render ────────────────────────────────────────────────────── */

  return (
    <div>
      {/* Sticky range selector + export buttons */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-zinc-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 dark:supports-[backdrop-filter]:bg-zinc-900/75 px-2 sm:px-4 py-2 border-b border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500 mr-1">
          Rango:
        </span>
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => updateRange(r.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              rangeKey === r.key
                ? "text-white"
                : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
            }`}
            style={rangeKey === r.key ? { backgroundColor: ACCENT } : undefined}
          >
            {r.label}
          </button>
        ))}
        {/* Solo lectura no exporta datos ni genera PDFs (P-FRA-13). */}
        {!esSoloLectura(userInfo) && (
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={!hasLeads || exportingCSV}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Descargar CSV"
          >
            <TableCellsIcon className="h-4 w-4" />
            {exportingCSV ? "Descargando…" : "CSV"}
          </button>
          <button
            type="button"
            onClick={handleExportPDF}
            disabled={!hasLeads || exportingPDF}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: ACCENT }}
            title="Descargar PDF"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            {exportingPDF ? "Generando…" : "PDF"}
          </button>
        </div>
        )}
      </div>

      <div className="p-2 sm:p-4 space-y-4">
      {/* Empty state */}
      {!hasLeads && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ChartBarIcon className="h-12 w-12 text-zinc-300 dark:text-zinc-600" />
          <p className="mt-3 text-sm text-zinc-500">
            Sin leads en este período. Cambia el rango o crea uno nuevo.
          </p>
        </div>
      )}

      {/* KPI cards */}
      {hasLeads && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Total de leads" value={stats.total} />
          <KpiCard label="Ganados" value={stats.ganados} />
          <KpiCard label="Perdidos" value={stats.perdidos} />
          <KpiCard
            label="Tasa de cierre"
            value={fmtPct(stats.closeRate)}
            hint="ganados / (ganados + perdidos)"
          />
          <KpiCard
            label="Conversión a evento"
            value={fmtPct(stats.conversionRate)}
            hint="leads convertidos a evento"
          />
          <KpiCard
            label="Cotizaciones"
            value={stats.cotizaciones}
            hint="generadas en el rango"
          />
        </div>
      )}

      {/* Charts */}
      {hasLeads && (
        <>
          {/* Leads over time */}
          <Section title={`Leads creados ${stats.bucketBy === "day" ? "por día" : "por mes"}`}>
            <div className="h-64 w-full">
              <ResponsiveContainer>
                <BarChart data={stats.byTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    interval={stats.byTime.length > 30 ? Math.floor(stats.byTime.length / 12) : 0}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={ACCENT} name="Leads" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          {/* Origin + status funnel side-by-side on wide screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Origen de leads">
              <div style={{ height: Math.max(180, 60 + stats.byOrigin.length * 32) }} className="w-full">
                <ResponsiveContainer>
                  <BarChart
                    data={stats.byOrigin}
                    layout="vertical"
                    margin={{ top: 8, right: 40, bottom: 24, left: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis
                      dataKey="label"
                      type="category"
                      tick={{ fontSize: 11 }}
                      width={110}
                    />
                    <Tooltip
                      formatter={(value, _name, p) => {
                        const pct = p?.payload?.pct;
                        return [`${value}${pct != null ? ` (${Math.round(pct * 100)}%)` : ""}`, "Leads"];
                      }}
                    />
                    <Bar dataKey="count" name="Leads">
                      {stats.byOrigin.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Embudo por estado">
              <div style={{ height: Math.max(180, 60 + stats.byStatus.length * 32) }} className="w-full">
                <ResponsiveContainer>
                  <BarChart
                    data={stats.byStatus}
                    layout="vertical"
                    margin={{ top: 8, right: 40, bottom: 24, left: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={110} />
                    <Tooltip />
                    <Bar dataKey="count" fill={ACCENT} name="Leads" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          {/* Detalle por origen */}
          <Section title="Detalle por origen">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-zinc-200 dark:border-zinc-700">
                  <tr>
                    <SortHeader k="label" label="Origen" />
                    <SortHeader k="count" label="Leads" align="right" />
                    <SortHeader k="pct" label="% del total" align="right" />
                    <SortHeader k="ganados" label="Ganados" align="right" />
                    <SortHeader k="perdidos" label="Perdidos" align="right" />
                    <SortHeader k="closeRate" label="Tasa de cierre" align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedByOrigin.map((o, i) => (
                    <tr key={o.label} className={i % 2 === 0 ? "bg-zinc-50/40 dark:bg-zinc-800/40" : ""}>
                      <td className="px-3 py-1.5 text-zinc-700 dark:text-zinc-300">{o.label}</td>
                      <td className="px-3 py-1.5 text-right">{o.count}</td>
                      <td className="px-3 py-1.5 text-right">{fmtPct(o.pct)}</td>
                      <td className="px-3 py-1.5 text-right">{o.ganados}</td>
                      <td className="px-3 py-1.5 text-right">{o.perdidos}</td>
                      <td className="px-3 py-1.5 text-right">{fmtPct(o.closeRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Top 5 temas */}
          {stats.byTopic.length > 0 && (
            <Section title="Top 5 temas">
              <div className="h-56 w-full">
                <ResponsiveContainer>
                  <BarChart data={stats.byTopic} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip />
                    <Bar dataKey="count" fill={ACCENT} name="Leads" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}
        </>
      )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
    <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-3">{title}</div>
    {children}
  </div>
);

export default LeadStats;

// Exported for use by sibling utils (PDF/CSV).
export { toJsDate, isContactFormLead, bucketOrigin, RANGES };
